import type { ServiceType } from "../DTOs/SundayService";

/**
 * Service types the user can choose in the form. "manual" is the legacy default
 * and is not offered explicitly – it is treated like "sunday-morning" in the UI.
 */
export const SELECTABLE_SERVICE_TYPES: ServiceType[] = [
    "sunday-morning",
    "sunday-afternoon",
    "saturday",
    "wednesday",
    "weekday-special",
];

/**
 * Suggests a service type from the weekday of the given date.
 * Wednesday → Bibelstunde, Saturday → Wochenschluss, Sunday → Sonntag Vormittag,
 * any other weekday → Sondergottesdienst (a holiday/special service). The user can override.
 */
export function inferServiceType(date: Date): ServiceType {
    const day = date.getDay(); // 0=So, 3=Mi, 6=Sa
    if (day === 3) return "wednesday";
    if (day === 6) return "saturday";
    if (day === 0) return "sunday-morning"; // user can switch to sunday-afternoon
    // Mo, Di, Do, Fr → no standard service → suggest a special service
    return "weekday-special";
}

/** Service types the choir sings at when the Gemeinde has not chosen any (same as the functions). */
export const DEFAULT_CHOIR_SERVICE_TYPES: ServiceType[] = ["sunday-morning"];

/** The Gemeinde setting `choirServiceTypes`, read defensively; mirrors functions/src/choirPlan.ts. */
export function choirServiceTypesOf(value: unknown): ServiceType[] {
    const valid = new Set<string>(SELECTABLE_SERVICE_TYPES);
    const types = Array.isArray(value) ? value.filter((v): v is ServiceType => typeof v === "string" && valid.has(v)) : [];
    return types.length > 0 ? types : DEFAULT_CHOIR_SERVICE_TYPES;
}

/** Legacy "manual" services count as Sunday morning. */
export function effectiveServiceType(type: ServiceType | undefined): ServiceType {
    return !type || type === "manual" ? "sunday-morning" : type;
}

/**
 * How the service form offers choir songs (GOD-49):
 * - "required": Sunday morning at a choir Gemeinde – the songs are part of the order of service
 * - "toggle": may have songs, switched on per service (special services; Sunday morning without choir)
 * - "optional": a choir type of this Gemeinde – song fields shown, none required
 * - "none": no choir at this kind of service (fields stay visible if the service already has songs)
 */
export type ChoirMode = "required" | "toggle" | "optional" | "none";

export function choirModeFor(type: ServiceType | undefined, choirTypes: ServiceType[], hasSongs: boolean): ChoirMode {
    const effective = effectiveServiceType(type);
    const isChoirType = choirTypes.includes(effective);
    if (effective === "sunday-morning") return isChoirType ? "required" : "toggle";
    if (effective === "weekday-special") return "toggle";
    if (isChoirType || hasSongs) return "optional";
    return "none";
}

/** Whether a service of this type can show choir songs in the list. */
export function canHaveChoir(type: ServiceType | undefined, choirTypes: ServiceType[], hasSongs: boolean): boolean {
    return hasSongs || choirTypes.includes(effectiveServiceType(type)) || effectiveServiceType(type) === "weekday-special";
}
